import { useState, isValidElement } from 'react'
import styled from 'styled-components'
import * as FiIcons from 'react-icons/fi'

/**
 * Componente MobileFilters per gestire filtri collassabili su mobile
 *
 * Props:
 * @param {ReactNode|Object} searchBar - Campo di ricerca (sempre visibile)
 *   - Come JSX: searchBar={<ContentStyle.Search ... />}
 *   - Come Object: searchBar={{ placeholder: '...', value: '', onChange: (e) => {} }}
 * @param {ReactNode} children - Filtri dropdown (collassabili)
 * @param {number} activeFiltersCount - Numero di filtri attivi
 * @param {ReactNode} resetButton - Bottone reset (opzionale, mostrato nel pannello)
 * @param {ReactNode} actionButton - Bottone azione principale (es. "Nuovo", sempre visibile)
 * @param {Array} buttons - Array di bottoni [{label, icon, onClick, type}]
 * @param {ReactNode} backButton - Bottone indietro (sempre visibile)
 */

const MobileFiltersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: ${props => props.theme.colors.background.primary};
`

const MobileFiltersTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const MobileFiltersActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const MobileFiltersSearchRow = styled.div`
  width: 100%;

  input {
    width: 100%;
    max-width: 100%;
  }
`

const MobileFiltersToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0 0.75rem;
  height: 30px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  background: ${props => props.isOpen
    ? props.theme.colors.semantic.primary
    : props.theme.colors.background.primary};
  color: ${props => props.isOpen
    ? props.theme.colors.text.inverse
    : props.theme.colors.text.secondary};
  font-size: 0.8rem;
  font-weight: 500;
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:active {
    transform: scale(0.97);
  }

  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    width: 14px;
    height: 14px;
  }
`

const MobileFiltersBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${props => props.theme.colors.semantic.info};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
`

const MobileFiltersPanel = styled.div`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.radius.input};
  border: 1px solid ${props => props.theme.colors.border.light};
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Stile i select figli per occupare tutta la larghezza */
  select {
    width: 100%;
    min-width: unset;
  }
`

const MobileFiltersPanelRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.75rem;
    color: ${props => props.theme.colors.text.muted};
    font-weight: 500;
  }
`

const MobileFiltersPanelActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`

const MobileSearchInput = styled.input`
  width: 100%;
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.875rem;
  font-family: ${props => props.theme.typography.fontFamily};
  outline: none;
  transition: ${props => props.theme.transitions.default};

  &:hover {
    border-color: ${props => props.theme.colors.text.tertiary};
    background: ${props => props.theme.colors.background.hover};
  }

  &:focus {
    border-color: ${props => props.theme.colors.text.primary};
    background: ${props => props.theme.colors.background.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`

const MobileActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0 0.75rem;
  height: 30px;
  border: 1px solid ${props => props.buttonType === 'primary'
    ? props.theme.colors.semantic.primary
    : props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.button};
  background: ${props => props.buttonType === 'primary'
    ? 'transparent'
    : props.theme.colors.background.primary};
  color: ${props => props.buttonType === 'primary'
    ? props.theme.colors.semantic.primary
    : props.theme.colors.text.secondary};
  font-size: 0.8rem;
  font-weight: 500;
  font-family: ${props => props.theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:active {
    transform: scale(0.97);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const MobileFilterSelect = styled.select`
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  background: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.primary};
  font-size: 0.875rem;
  font-family: ${props => props.theme.typography.fontFamily};
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${props => props.theme.colors.semantic.primary};
  }
`

const MobileFilters = ({
  searchBar,
  children,
  activeFiltersCount = 0,
  resetButton,
  actionButton,
  buttons,
  actions,
  filterGroups,
  backButton
}) => {
  const [isOpen, setIsOpen] = useState(false)

  // Determina se searchBar è un oggetto di configurazione o un ReactNode
  const isSearchBarConfig = searchBar && typeof searchBar === 'object' && !isValidElement(searchBar)

  // Handler wrapper per searchBar
  const handleSearchChange = (e) => {
    if (searchBar?.onChange) searchBar.onChange(e)
  }

  // Render della searchbar
  const renderSearchBar = () => {
    if (!searchBar) return null
    if (isSearchBarConfig) {
      return (
        <MobileSearchInput
          type='text'
          placeholder={searchBar.placeholder}
          value={searchBar.value}
          onChange={handleSearchChange}
        />
      )
    }
    return searchBar
  }

  // Handler wrapper per actionButton
  const handleActionButtonClick = () => {
    if (actionButton?.onClick) actionButton.onClick()
  }

  // Handler wrapper per bottoni lista
  const handleButtonClick = (btn) => () => {
    if (btn?.onClick) btn.onClick()
  }

  // Render dei bottoni azione - supporta sia 'buttons' che 'actions'
  const renderActionButtons = () => {
    // Se è passato actionButton
    if (actionButton) {
      // Se è un oggetto config {icon, onClick, label?, type?}, renderizza come MobileActionButton
      if (typeof actionButton === 'object' && !isValidElement(actionButton) && actionButton.onClick) {
        return (
          <MobileActionButton
            buttonType={actionButton.type}
            onClick={handleActionButtonClick}
          >
            {actionButton.icon}
            {actionButton.label}
          </MobileActionButton>
        )
      }
      // Altrimenti è JSX, renderizzalo direttamente
      return <div>{actionButton}</div>
    }
    // Usa buttons oppure actions (sono equivalenti)
    const buttonList = buttons || actions
    if (buttonList && Array.isArray(buttonList) && buttonList.length > 0) {
      return buttonList.map((btn, index) => (
        <MobileActionButton
          key={index}
          buttonType={btn.type}
          onClick={handleButtonClick(btn)}
        >
          {btn.icon}
          {btn.label}
        </MobileActionButton>
      ))
    }
    return null
  }

  // Handler wrapper per filterGroups
  const handleFilterChange = (filter) => (e) => {
    if (filter?.onChange) filter.onChange(e)
  }

  // Render dei filtri dal filterGroups
  const renderFilterGroups = () => {
    if (!filterGroups || !Array.isArray(filterGroups)) return null
    return filterGroups.map((filter, index) => (
      <MobileFiltersPanelRow key={index}>
        <label>{filter.name}</label>
        <MobileFilterSelect
          value={filter.value}
          onChange={handleFilterChange(filter)}
        >
          {filter.options && filter.options.map((opt, i) => (
            <option key={i} value={opt.value}>{opt.label}</option>
          ))}
        </MobileFilterSelect>
      </MobileFiltersPanelRow>
    ))
  }

  // Determina se ci sono filtri da mostrare
  const hasFilters = (filterGroups && filterGroups.length > 0) ||
                     (children && (Array.isArray(children) ? children.length > 0 : true))

  return (
    <MobileFiltersContainer>
      <MobileFiltersTopRow>
        {backButton && (
          <div>{backButton}</div>
        )}
        <MobileFiltersActions>
          {hasFilters && (
            <MobileFiltersToggle
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              <FiIcons.FiFilter size={16} />
              Filtri
              {activeFiltersCount > 0 && (
                <MobileFiltersBadge>{activeFiltersCount}</MobileFiltersBadge>
              )}
              <FiIcons.FiChevronDown size={16} />
            </MobileFiltersToggle>
          )}
          {renderActionButtons()}
        </MobileFiltersActions>
      </MobileFiltersTopRow>

      <MobileFiltersSearchRow>
        {renderSearchBar()}
      </MobileFiltersSearchRow>

      {hasFilters && (
        <MobileFiltersPanel isOpen={isOpen}>
          {renderFilterGroups()}
          {children}
          {resetButton && (
            <MobileFiltersPanelActions>
              {resetButton}
            </MobileFiltersPanelActions>
          )}
        </MobileFiltersPanel>
      )}
    </MobileFiltersContainer>
  )
}

export default MobileFilters
export {
  MobileFiltersContainer,
  MobileFiltersTopRow,
  MobileFiltersSearchRow,
  MobileFiltersToggle,
  MobileFiltersBadge,
  MobileFiltersPanel,
  MobileFiltersPanelRow,
  MobileFiltersPanelActions
}
